import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import BlacksmithWorkshop from '../components/BlacksmithWorkshop';
import ConnectWalletModal from '../components/ConnectWalletModal';
import MultichainStatus from '../components/MultichainStatus';
import { createICSpicyActor } from '../services/icspicy';
import { Principal } from '@dfinity/principal';
import { useAuth } from '../auth/useAuth';

export default function WalletPage() {
  const { agent, principal, isAuthenticated } = useAuth();
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [tokens, setTokens] = useState<bigint[]>([]);
  const [nftMetadata, setNftMetadata] = useState<Map<bigint, any>>(new Map());
  const [transferTo, setTransferTo] = useState('');
  const [transferTokenId, setTransferTokenId] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [multichainDeployments, setMultichainDeployments] = useState<Map<bigint, any>>(new Map());
  const [deploying, setDeploying] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (!agent || !isAuthenticated || !principal) return;
    loadTokens(agent, principal);
  }, [agent, isAuthenticated, principal]);

  useEffect(() => {
    if (!agent || !isAuthenticated || !principal || tokens.length === 0) return;
    loadMultichainDeployments(agent);
  }, [agent, isAuthenticated, principal, tokens]);

  const loadTokens = async (agent: any, principal: Principal) => {
    try {
      const actor = await createICSpicyActor(agent);
      const userTokens = await actor.get_user_tokens(principal);
      setTokens(userTokens);
      
      // Load metadata for each token
      const metadataMap = new Map();
      for (const tokenId of userTokens) {
        try {
          const metadata = await actor.get_nft_metadata(tokenId);
          if (metadata && metadata.length > 0) {
            metadataMap.set(tokenId, JSON.parse(metadata[0]));
          }
        } catch (err) {
          console.error(`Failed to load metadata for token ${tokenId}:`, err);
        }
      }
      setNftMetadata(metadataMap);
    } catch (err) {
      console.error('Failed to load tokens:', err);
    }
  };

  const loadMultichainDeployments = async (agent: any) => {
    if (!agent || tokens.length === 0) return;
    try {
      const actor = await createICSpicyActor(agent);
      const deploymentMap = new Map();
      for (const tokenId of tokens) {
        try {
          const deployment = await actor.get_multichain_deployment(tokenId);
          if (deployment) {
            deploymentMap.set(tokenId, deployment);
          }
        } catch (err) {
          console.error(`Failed to load multichain deployment for token ${tokenId}:`, err);
        }
      }
      setMultichainDeployments(deploymentMap);
    } catch (err) {
      console.error('Failed to load multichain deployments:', err);
    }
  };

  const handleRetryDeployment = async (tokenId: bigint, chain: 'evm' | 'solana' | 'bitcoin') => {
    if (!agent) return;
    const key = `${tokenId}_${chain}`;
    setDeploying({ ...deploying, [key]: true });
    setError(null);
    try {
      const actor = await createICSpicyActor(agent);
      let result;
      if (chain === 'evm') {
        result = await actor.deploy_evm_contract(tokenId);
      } else if (chain === 'solana') {
        result = await actor.deploy_solana_program(tokenId);
      } else {
        result = await actor.deploy_bitcoin_ordinal(tokenId);
      }
      setSuccess(`${chain.toUpperCase()} retry initiated: ${result}`);
      loadMultichainDeployments(agent);
    } catch (err: any) {
      setError(err.message || `Failed to retry ${chain} deployment`);
    } finally {
      setDeploying({ ...deploying, [key]: false });
    }
  };

  const handleConnect = () => {
    setShowConnectModal(true);
  };

  const handleTransfer = async () => {
    if (!agent || !isAuthenticated || !transferTo || !transferTokenId) {
      setError('Please fill in all fields');
      return;
    }

    setTransferring(true);
    setError(null);
    setSuccess(null);

    try {
      const actor = await createICSpicyActor(agent);
      const recipient = Principal.fromText(transferTo);
      const tokenId = BigInt(transferTokenId);
      
      const result = await actor.transfer_nft(recipient, tokenId);
      
      if (result) {
        setSuccess(`NFT #${transferTokenId} transferred successfully`);
        setTransferTo('');
        setTransferTokenId('');
        if (principal) {
          loadTokens(agent, principal);
        }
      } else {
        setError('Transfer failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to transfer NFT');
    } finally {
      setTransferring(false);
    }
  };

  return (
    <BlacksmithWorkshop>
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          <div className="glass-dark rounded-2xl p-8 mb-6">
            <h1 className="text-4xl font-bold mb-6 text-gold">My Wallet</h1>
            
            {!isAuthenticated ? (
              <div className="text-center">
                <button
                  onClick={handleConnect}
                  className="bg-gold hover:bg-yellow-600 text-white font-bold py-3 px-6 rounded-lg transition-all"
                >
                  Connect Wallet
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <p className="text-gray-300 mb-2">Your Principal:</p>
                  <p className="text-sm font-mono break-all bg-black/30 p-2 rounded">
                    {principal?.toText()}
                  </p>
                  <p className="text-gold mt-2">Balance: {tokens.length} NFT(s)</p>
                </div>

                {/* Transfer Section */}
                <div className="mb-6 p-4 bg-black/30 rounded-lg">
                  <h2 className="text-xl font-semibold mb-4 text-gold">Transfer NFT</h2>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-gray-300 mb-2">Token ID</label>
                      <input
                        type="number"
                        value={transferTokenId}
                        onChange={(e) => setTransferTokenId(e.target.value)}
                        placeholder="Enter token ID"
                        className="w-full px-4 py-2 rounded-lg bg-black/30 text-white border border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Recipient Address</label>
                      <input
                        type="text"
                        value={transferTo}
                        onChange={(e) => setTransferTo(e.target.value)}
                        placeholder="Enter recipient principal ID"
                        className="w-full px-4 py-2 rounded-lg bg-black/30 text-white border border-gray-600"
                      />
                    </div>
                    <button
                      onClick={handleTransfer}
                      disabled={transferring || !transferTo || !transferTokenId}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-lg transition-all"
                    >
                      {transferring ? 'Transferring...' : 'Transfer NFT'}
                    </button>
                  </div>
                </div>

                {/* NFT Gallery */}
                <div>
                  <h2 className="text-2xl font-semibold mb-4 text-gold">My NFTs</h2>
                  {tokens.length === 0 ? (
                    <p className="text-gray-400">No NFTs in your wallet</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {tokens.map((tokenId) => {
                        const metadata = nftMetadata.get(tokenId);
                        
                        return (
                          <motion.div
                            key={tokenId.toString()}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="glass-dark rounded-xl p-6"
                          >
                            <div className="mb-4">
                              <h3 className="text-lg font-bold text-gold mb-2">
                                Token #{tokenId.toString()}
                              </h3>
                              {metadata && (
                                <div className="text-sm text-gray-300 space-y-1">
                                  <p>Rarity: {metadata.rarity?.toFixed(2) || 'N/A'}</p>
                                  <p>Traits: {metadata.traits?.length || 0}</p>
                                </div>
                              )}
                            </div>

                            {/* ICP Badge (Primary) */}
                            <div className="mb-4 p-2 bg-blue-900/30 border border-blue-500 rounded-lg">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-blue-300">ICP (Primary)</span>
                                <span className="text-xs text-green-400">✓ Native</span>
                              </div>
                            </div>

                            {/* Multichain Deployment Status - Auto-deployed on mint */}
                            <div className="mb-4">
                              <div className="text-xs font-semibold text-gray-400 mb-2">Multichain Status (Auto-deployed):</div>
                              <MultichainStatus
                                tokenId={tokenId}
                                agent={agent}
                                onRetry={handleRetryDeployment}
                              />
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 bg-red-900/30 border border-red-500 rounded-lg text-red-200 mb-4"
            >
              {error}
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 bg-green-900/30 border border-green-500 rounded-lg text-green-200"
            >
              {success}
            </motion.div>
          )}
        </motion.div>
      </div>
    </BlacksmithWorkshop>
  );
}






